import React, { useEffect, useState, useCallback } from 'react';
import { Plus, X, Package, Trash2, StickyNote, Link2, Check, AlertCircle, Calendar, ChevronDown, ChevronRight } from 'lucide-react';
import { adminApi, formatApiError, DELIVERY_STAGES } from '../../utils/adminApi';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const DATE_PRESETS = [
  { id: 'all', label: 'All time', days: null },
  { id: '7d', label: 'Last 7 days', days: 7 },
  { id: '30d', label: 'Last 30 days', days: 30 },
  { id: '90d', label: 'Last 90 days', days: 90 },
];

const OrdersTab = ({ onToast, onUnseenChange }) => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const sinceIso = () => {
    const preset = DATE_PRESETS.find((p) => p.id === datePreset);
    if (!preset || !preset.days) return undefined;
    const d = new Date();
    d.setDate(d.getDate() - preset.days);
    return d.toISOString();
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, stat] = await Promise.all([
        adminApi.listOrdersFiltered({ stage: stageFilter || undefined, since: sinceIso() }),
        adminApi.orderStats(),
      ]);
      setOrders(list);
      setStats(stat);
      onUnseenChange?.(stat.unseen);
    } catch (err) {
      setError(formatApiError(err, 'Could not load orders.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageFilter, datePreset]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleStageChange = async (id, stage) => {
    try {
      await adminApi.updateOrder(id, { current_stage: stage });
      onToast?.('Stage updated');
      refresh();
    } catch (err) { setError(formatApiError(err)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this order? This cannot be undone.')) return;
    try {
      await adminApi.deleteOrder(id);
      onToast?.('Order removed');
      if (expandedId === id) setExpandedId(null);
      refresh();
    } catch (err) { setError(formatApiError(err)); }
  };

  const handleExpand = async (order) => {
    const next = expandedId === order.id ? null : order.id;
    setExpandedId(next);
    if (next && !order.seen) {
      try {
        await adminApi.markOrderSeen(order.id);
        refresh();
      } catch (err) { /* swallow */ }
    }
  };

  const handleMarkAllSeen = async () => {
    try {
      await adminApi.markAllOrdersSeen();
      onToast?.('All orders marked as seen');
      refresh();
    } catch (err) { setError(formatApiError(err)); }
  };

  const copyTrackingLink = (trackingId) => {
    const url = `${window.location.origin}/track?id=${trackingId}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url);
      onToast?.('Link copied');
    } else {
      // Fallback
      const t = document.createElement('textarea');
      t.value = url;
      document.body.appendChild(t);
      t.select();
      document.execCommand('copy');
      document.body.removeChild(t);
      onToast?.('Link copied');
    }
  };

  return (
    <div data-testid="orders-tab">
      {/* Filters + actions */}
      <div className="orders-toolbar">
        <div className="orders-toolbar-left">
          <label className="admin-field orders-filter">
            <span className="admin-field-label">Stage</span>
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              data-testid="orders-filter-stage"
            >
              <option value="">All stages</option>
              {DELIVERY_STAGES.map((s) => (
                <option key={s} value={s}>
                  {s}{stats ? ` (${stats.by_stage?.[s] ?? 0})` : ''}
                </option>
              ))}
            </select>
          </label>

          <div className="orders-date-presets" role="tablist">
            <Calendar size={14} color="var(--text-secondary)" />
            {DATE_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={`admin-filter-pill ${datePreset === p.id ? 'active' : ''}`}
                onClick={() => setDatePreset(p.id)}
                data-testid={`orders-filter-date-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="orders-toolbar-right">
          {stats && stats.unseen > 0 && (
            <button
              type="button"
              className="btn-secondary admin-icon-btn"
              onClick={handleMarkAllSeen}
              data-testid="orders-mark-all-seen"
            >
              <Check size={14} /> Mark all seen ({stats.unseen})
            </button>
          )}
          <button
            className="btn-primary"
            onClick={() => setShowCreate(true)}
            data-testid="admin-create-order-btn"
          >
            <Plus size={14} /> New Order
          </button>
        </div>
      </div>

      {error && <div className="admin-error" data-testid="orders-error"><AlertCircle size={14} /> {error}</div>}
      {loading && <p className="body-regular">Loading…</p>}

      {!loading && orders.length === 0 && (
        <p className="body-regular admin-empty">
          {stageFilter || datePreset !== 'all'
            ? 'No orders match the current filters.'
            : 'No orders yet. Convert an inquiry or create one manually.'}
        </p>
      )}

      {!loading && orders.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-table" data-testid="admin-orders-table">
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>Tracking</th>
                <th>Customer</th>
                <th>Piece</th>
                <th>Stage</th>
                <th>Created</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <React.Fragment key={o.id}>
                  <tr
                    data-testid={`order-row-${o.id}`}
                    className={expandedId === o.id ? 'order-row-expanded' : ''}
                  >
                    <td>
                      <button
                        className="admin-icon-only order-expand"
                        onClick={() => handleExpand(o)}
                        title={expandedId === o.id ? 'Collapse' : 'Expand'}
                        data-testid={`order-expand-${o.id}`}
                      >
                        {expandedId === o.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </td>
                    <td>
                      <span className="tracking-id-cell">
                        {!o.seen && <span className="order-new-dot" title="New order" data-testid={`order-new-dot-${o.id}`} />}
                        <strong>{o.tracking_id}</strong>
                      </span>
                    </td>
                    <td>
                      <strong>{o.customer_name}</strong>
                      <div className="admin-cell-sub">{o.customer_email}</div>
                    </td>
                    <td>
                      <strong>{o.piece_name}</strong>
                      {(o.fabric || o.embroidery) && (
                        <div className="admin-cell-sub">
                          {[o.fabric, o.embroidery].filter(Boolean).join(' • ')}
                        </div>
                      )}
                    </td>
                    <td>
                      <select
                        value={o.current_stage}
                        onChange={(e) => handleStageChange(o.id, e.target.value)}
                        className="admin-select"
                        data-testid={`order-stage-${o.id}`}
                      >
                        {DELIVERY_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="admin-cell-meta">{formatDate(o.created_at)}</td>
                    <td className="admin-cell-meta">
                      <span className="notes-badge" data-testid={`order-notes-count-${o.id}`}>
                        <StickyNote size={12} /> {o.timeline?.length || 0}
                      </span>
                    </td>
                    <td>
                      <div className="order-row-actions">
                        <button
                          className="admin-icon-only"
                          onClick={() => copyTrackingLink(o.tracking_id)}
                          title="Copy customer tracking link"
                          data-testid={`order-copy-link-${o.id}`}
                        >
                          <Link2 size={14} />
                        </button>
                        <button
                          className="admin-icon-only"
                          onClick={() => handleDelete(o.id)}
                          title="Delete"
                          data-testid={`order-delete-${o.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === o.id && (
                    <tr className="order-detail-row" data-testid={`order-detail-${o.id}`}>
                      <td colSpan={8}>
                        <OrderDetailPanel
                          order={o}
                          onRefresh={refresh}
                          onToast={onToast}
                          setError={setError}
                          copyTrackingLink={copyTrackingLink}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateOrderModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); onToast?.('Order created'); refresh(); }}
        />
      )}
    </div>
  );
};

const OrderDetailPanel = ({ order, onRefresh, onToast, setError, copyTrackingLink }) => {
  const [noteText, setNoteText] = useState('');
  const [adding, setAdding] = useState(false);

  const addNote = async () => {
    const txt = noteText.trim();
    if (!txt) return;
    setAdding(true);
    try {
      await adminApi.addOrderNote(order.id, txt);
      setNoteText('');
      onToast?.('Note added');
      onRefresh();
    } catch (err) { setError(formatApiError(err, 'Could not add note.')); }
    finally { setAdding(false); }
  };

  const removeNote = async (idx) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await adminApi.deleteOrderNote(order.id, idx);
      onToast?.('Note removed');
      onRefresh();
    } catch (err) { setError(formatApiError(err)); }
  };

  const timeline = order.timeline || [];
  const trackingUrl = `${window.location.origin}/track?id=${order.tracking_id}`;

  return (
    <div className="order-detail-panel">
      <div className="order-detail-grid">
        {/* Left — details */}
        <div>
          <p className="navigation-text" style={{ color: 'var(--accent-gold)' }}>Customer</p>
          <h4 className="heading-3" style={{ marginTop: 6 }}>{order.customer_name}</h4>
          <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
            <a href={`mailto:${order.customer_email}`} className="admin-link">{order.customer_email}</a>
            <br />
            <a href={`tel:${order.customer_phone}`} className="admin-link">{order.customer_phone}</a>
          </p>
          {order.notes && (
            <>
              <p className="navigation-text" style={{ color: 'var(--accent-gold)', marginTop: 20 }}>Original notes</p>
              <p className="body-small" style={{ color: 'var(--text-primary)', marginTop: 6, fontStyle: 'italic' }}>
                {order.notes}
              </p>
            </>
          )}
          <div className="tracking-link-box">
            <span className="admin-field-label">Customer tracking link</span>
            <div className="tracking-link-row">
              <code data-testid={`order-tracking-url-${order.id}`}>{trackingUrl}</code>
              <button
                type="button"
                className="btn-secondary admin-row-btn"
                onClick={() => copyTrackingLink(order.tracking_id)}
                data-testid={`order-copy-link-detail-${order.id}`}
              >
                <Link2 size={12} /> Copy
              </button>
            </div>
          </div>
        </div>

        {/* Right — notes timeline */}
        <div>
          <p className="navigation-text" style={{ color: 'var(--accent-gold)' }}>Internal timeline</p>
          <p className="body-small" style={{ color: 'var(--text-secondary)', marginTop: 4 }}>
            Private notes visible to the atelier only.
          </p>

          <div className="notes-add">
            <textarea
              rows={2}
              placeholder="Add an internal note… e.g. 'Called customer, confirmed embroidery colour.'"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              data-testid={`order-note-input-${order.id}`}
            />
            <button
              type="button"
              className="btn-primary"
              onClick={addNote}
              disabled={adding || !noteText.trim()}
              data-testid={`order-note-submit-${order.id}`}
            >
              <StickyNote size={14} /> {adding ? 'Adding…' : 'Add Note'}
            </button>
          </div>

          {timeline.length === 0 ? (
            <p className="body-small" style={{ color: 'var(--text-light)', fontStyle: 'italic', marginTop: 16 }}>
              No notes yet.
            </p>
          ) : (
            <ul className="notes-timeline" data-testid={`order-timeline-${order.id}`}>
              {timeline.map((n, idx) => (
                <li key={idx} className="notes-timeline-item">
                  <div className="notes-timeline-dot"><Package size={10} /></div>
                  <div className="notes-timeline-body">
                    <p className="body-regular">{n.text}</p>
                    <div className="notes-timeline-meta">
                      <span>{formatDate(n.added_at)}</span>
                      <button
                        type="button"
                        className="notes-timeline-delete"
                        onClick={() => removeNote(idx)}
                        title="Delete note"
                        data-testid={`order-note-delete-${order.id}-${idx}`}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const CreateOrderModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    piece_name: '', fabric: '', embroidery: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    try {
      await adminApi.createOrder({
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        piece_name: form.piece_name,
        fabric: form.fabric || undefined,
        embroidery: form.embroidery || undefined,
        notes: form.notes || undefined,
      });
      onCreated();
    } catch (e2) { setErr(formatApiError(e2, 'Could not create order.')); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} data-testid="admin-create-order-modal">
        <button className="admin-modal-close" onClick={onClose}><X size={20} /></button>
        <h2 className="heading-2" style={{ marginBottom: 8 }}>New Commission</h2>
        <p className="body-small" style={{ marginBottom: 28, color: 'var(--text-secondary)' }}>
          A tracking ID will be generated automatically.
        </p>
        <form onSubmit={submit} className="admin-form">
          <div className="admin-form-grid">
            <label className="admin-field">
              <span className="admin-field-label">Customer name</span>
              <input required value={form.customer_name} onChange={update('customer_name')} data-testid="order-input-name" />
            </label>
            <label className="admin-field">
              <span className="admin-field-label">Email</span>
              <input type="email" required value={form.customer_email} onChange={update('customer_email')} data-testid="order-input-email" />
            </label>
            <label className="admin-field">
              <span className="admin-field-label">Phone</span>
              <input required value={form.customer_phone} onChange={update('customer_phone')} placeholder="+91..." data-testid="order-input-phone" />
            </label>
            <label className="admin-field">
              <span className="admin-field-label">Piece</span>
              <input required value={form.piece_name} onChange={update('piece_name')} data-testid="order-input-piece" />
            </label>
            <label className="admin-field">
              <span className="admin-field-label">Fabric (optional)</span>
              <input value={form.fabric} onChange={update('fabric')} data-testid="order-input-fabric" />
            </label>
            <label className="admin-field">
              <span className="admin-field-label">Embroidery (optional)</span>
              <input value={form.embroidery} onChange={update('embroidery')} data-testid="order-input-embroidery" />
            </label>
          </div>
          <label className="admin-field">
            <span className="admin-field-label">Notes (optional)</span>
            <textarea rows="3" value={form.notes} onChange={update('notes')} data-testid="order-input-notes" />
          </label>
          {err && <div className="admin-error"><AlertCircle size={14} /> {err}</div>}
          <div className="admin-modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting} data-testid="order-submit">
              {submitting ? 'Creating…' : 'Create Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrdersTab;
